import { Link } from "react-router-dom";
import Container from "@/components/base/Container";
import PageHeader from "@/components/feature/PageHeader";
import Seo from "@/components/feature/Seo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Reveal from "@/components/feature/Reveal";
import SubmitForm from "@/pages/about/components/SubmitForm";
import { principles, siteBrand, siteStats, team, testimonials } from "@/mocks/site";

export default function AboutPage() {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://shenguashe.example.com";

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于深瓜社",
    description: siteBrand.intro,
    url: `${origin}${__BASE_PATH__}about`,
    inLanguage: "zh-CN",
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteBrand.name,
    url: `${origin}${__BASE_PATH__}`,
    email: siteBrand.email,
    slogan: siteBrand.slogan,
    description: siteBrand.intro,
    employee: team.map((member) => ({
      "@type": "Person",
      name: member.name,
      jobTitle: member.role,
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: `${origin}${__BASE_PATH__}` },
      { "@type": "ListItem", position: 2, name: "关于我们", item: `${origin}${__BASE_PATH__}about` },
    ],
  };

  return (
    <>
      <Seo
        title="关于我们｜深瓜社的内容原则与编辑团队 - 深瓜社"
        description="深瓜社是一个专注事件复盘的中文内容站。了解我们的品牌定位、内容原则、编辑团队与读者口碑，以及如何向我们提交可核对的热门大瓜线索。"
        keywords="大瓜深扒,深度解析,完整时间线,内容原则,编辑团队,线索投稿"
        path="/about"
        jsonLd={[aboutSchema, organizationSchema, breadcrumbSchema]}
      />
      <SiteHeader />
      <main>
        <PageHeader
          crumbs={[{ label: "关于我们" }]}
          eyebrow="关于我们"
          title="我们只做一件事：把时间排对"
          description={siteBrand.slogan}
        />

        <section className="w-full py-12 md:py-16">
          <Container>
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
              <Reveal className="lg:col-span-6">
                <div className="w-full overflow-hidden rounded-lg" style={{ aspectRatio: "4 / 3" }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20a%20quiet%20editorial%20desk%20with%20stacked%20papers%2C%20a%20magnifying%20glass%2C%20a%20simple%20clock%20and%20a%20warm%20desk%20lamp%2C%20cream%20terracotta%20and%20forest%20green%20palette%2C%20abstract%20shapes%2C%20paper%20grain%20texture%2C%20no%20people&width=1200&height=900&seq=shenguashe-about-01&orientation=landscape"
                    alt="深瓜社编辑部工作台的插画"
                    title="深瓜社编辑部 - 事件复盘与时间线整理"
                    className="h-full w-full object-top"
                  />
                </div>
              </Reveal>
              <Reveal className="lg:col-span-6" delay={120}>
                <span className="text-xs font-medium tracking-[0.22em] text-primary-600">品牌介绍</span>
                <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  一个把过程放在结论之前的编辑部
                </h2>
                <div className="mt-5 space-y-4 text-sm leading-[1.9] text-foreground-700 md:text-[15px]">
                  <p>
                    {siteBrand.intro}
                  </p>
                  <p>
                    我们相信，大多数争议之所以反复，不是因为缺少立场，而是因为缺少顺序。同一句话放在不同的位置，
                    性质就完全不同。深瓜社做的事，就是先把顺序恢复出来，再让读者自己去判断。
                  </p>
                  <p>
                    我们是小团队，更新不快，但每一篇都愿意花时间做交叉验证。宁可写着「待确认」，也不写一个看起来完整的故事。
                  </p>
                </div>
                <div className="mt-8 grid grid-cols-2 gap-5 border-t border-background-200 pt-6 sm:grid-cols-4">
                  {siteStats.map((stat) => (
                    <div key={stat.label}>
                      <p className="font-heading text-2xl font-bold text-primary-600">{stat.value}</p>
                      <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </Container>
        </section>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <Container>
            <Reveal className="max-w-2xl">
              <span className="text-xs font-medium tracking-[0.22em] text-secondary-700">内容原则</span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                这四条规则，任何一篇稿子都不例外
              </h2>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {principles.map((item, index) => (
                <Reveal key={item.title} delay={index * 70}>
                  <div className="flex h-full gap-5 rounded-lg border border-background-200 bg-background-50 p-6">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-500 text-lg text-background-50">
                      <i className={item.icon}></i>
                    </span>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.description}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Container>
        </section>

        <section className="w-full py-12 md:py-16">
          <Container>
            <Reveal className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div>
                <span className="text-xs font-medium tracking-[0.22em] text-primary-600">编辑团队</span>
                <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  四个人的编辑部
                </h2>
              </div>
              <p className="max-w-md text-sm leading-relaxed text-foreground-600">
                我们不做独家，只做别人不愿意做的整理工作。
              </p>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {team.map((member, index) => (
                <Reveal key={member.name} delay={index * 70}>
                  <div className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-6">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 font-heading text-lg font-bold text-foreground-800">
                      {member.name.slice(0, 1)}
                    </span>
                    <h3 className="mt-5 font-heading text-base font-semibold text-foreground-950">{member.name}</h3>
                    <p className="mt-1 text-xs font-medium text-primary-600">{member.role}</p>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-600">{member.note}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </Container>
        </section>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <Container>
            <Reveal className="max-w-2xl">
              <span className="text-xs font-medium tracking-[0.22em] text-accent-700">读者口碑</span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                他们是这样形容这里的
              </h2>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
              {testimonials.map((item, index) => (
                <Reveal key={item.name} delay={index * 70}>
                  <figure className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-6">
                    <span className="font-heading text-2xl leading-none text-accent-500">&ldquo;</span>
                    <blockquote className="mt-3 flex-1 text-sm leading-[1.9] text-foreground-700">
                      {item.quote}
                    </blockquote>
                    <figcaption className="mt-5 flex items-center gap-3 border-t border-background-100 pt-4">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-100 font-heading text-sm font-bold text-accent-900">
                        {item.name.slice(0, 1)}
                      </span>
                      <span>
                        <span className="block text-sm font-medium text-foreground-900">{item.name}</span>
                        <span className="block text-xs text-foreground-500">{item.role}</span>
                      </span>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </Container>
        </section>

        <section id="submit" className="w-full scroll-mt-24 py-12 md:py-16">
          <Container>
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
              <Reveal className="lg:col-span-5">
                <span className="text-xs font-medium tracking-[0.22em] text-primary-600">联系与投稿</span>
                <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  有线索，就直接告诉我们
                </h2>
                <p className="mt-4 text-sm leading-[1.9] text-foreground-700">
                  我们不接受匿名指控，但接受未整理的材料。只要带上时间、地点与可核对的痕迹，
                  哪怕只是一张截图，我们也会认真对待。
                </p>
                <ul className="mt-6 space-y-4 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-700">
                      <i className="ri-mail-line"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">编辑部邮箱</span>
                      <a href={`mailto:${siteBrand.email}`} rel="nofollow" className="text-foreground-900 transition-colors hover:text-primary-700">
                        {siteBrand.email}
                      </a>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-700">
                      <i className="ri-wechat-line"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">公众号同步更新</span>
                      <span className="text-foreground-900">{siteBrand.wechat}</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-700">
                      <i className="ri-calendar-check-line"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">更新节奏</span>
                      <span className="text-foreground-900">{siteBrand.updateNote}</span>
                    </span>
                  </li>
                </ul>
                <p className="mt-6 rounded-md border border-background-200 bg-background-100 p-4 text-xs leading-relaxed text-foreground-600">
                  投稿前建议先看一眼
                  <Link to="/faq" className="mx-1 text-primary-700 underline underline-offset-4">
                    常见问题
                  </Link>
                  ，里面写清楚了我们核对线索的方式与边界。
                </p>
              </Reveal>

              <Reveal className="lg:col-span-7" delay={120}>
                <SubmitForm />
              </Reveal>
            </div>
          </Container>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}